import { safeMathRandom } from '@/utils/number.js'
/**
 * Capitalizes the first character of a string.
 *
 * @param str - The string to capitalize
 * @returns The capitalized string
 * @example
 * capitalize('hello') // 'Hello'
 */
export const capitalize = <T extends string>(str: T) =>
  `${str[0].toLocaleUpperCase()}${str.slice(1)}` as Capitalize<T>

/**
 * Returns the first character of a string, capitalized.
 *
 * @param str - The string to extract the initial from
 * @returns The capitalized initial character, or undefined if the string is empty
 * @example
 * initial('hello') // 'H'
 */
export const initial = (str: string) => str[0]?.toLocaleUpperCase()

/**
 * Generates a random alphanumeric string of a given length.
 *
 * @param length - The desired length of the string
 * @returns A random alphanumeric string of the specified length
 * @example
 * randomAlphaNumeric(8) // 'aZ3kLm9Q'
 */
export const randomAlphaNumeric = (
  length: number,
): string | Promise<string> => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''

  // If we have safeMathRandom already, return synchronously
  for (let i = 0; i < length; i++) {
    const entropy = Date.now() * safeMathRandom() * performance.now()
    const index = Math.floor(entropy % chars.length)
    result += chars[index]
  }

  return result
}

/**
 * Gets the initials from a name or array of names, up to a maximum length.
 *
 * @param name - The name string or array of name parts
 * @param maxLength - The maximum number of initials to return (default: 2)
 * @returns The initials as a string
 * @example
 * getInitials('John Doe') // 'JD'
 * getInitials(['Jane', 'Smith'], 1) // 'J'
 */
export const getInitials = (
  name: string | undefined | null | (string | undefined | null)[],
  maxLength = 2,
) => {
  if (!name?.length) {
    return ''
  }

  const parts = Array.isArray(name) ? name : name.split(' ')

  return parts
    .filter(Boolean)
    .map((part) => initial(part!))
    .join('')
    .substring(0, maxLength)
}

/**
 * Formats the full name for any entity that contains first and last name in the schema attributes.
 *
 * @param attributes - The attributes to get the full name from
 * @returns The full name
 * @example
 * getEntityFullName({ first_name: 'John', last_name: 'Doe' }) // 'John Doe'
 * getEntityFullName({ first_name: 'John' }) // 'John'
 * getEntityFullName({ last_name: 'Doe' }) // 'Doe'
 * getEntityFullName({}) // ''
 */
export const getEntityFullName = (attributes?: {
  first_name?: string
  last_name?: string
}) =>
  [attributes?.first_name, attributes?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()

/**
 * Converts all underscores in a string to hyphens.
 * @param str - The input string to convert
 * @returns A new string with all underscores replaced by hyphens
 * @example
 * ```ts
 * underscoreToHyphen('hello_world') // returns 'hello-world'
 * underscoreToHyphen('hello__world') // returns 'hello--world'
 * ```
 */
export const underscoreToHyphen = (str: string) => str.replace(/_/g, '-')

/**
 * Formats an ID to a string of a given length, padding with zeros if necessary.
 *
 * @param id - The ID to format
 * @param minLength - The minimum length of the formatted ID (default: 5)
 * @returns The formatted ID
 * @example
 * getFormattedId('123') // returns '00123'
 */
export const getFormattedId = (id: string, minLength = 5) => {
  return String(id).padStart(minLength, '0')
}

/**
 * Checks if a character is a Unicode whitespace character.
 *
 * Uses the Unicode `White_Space` property which matches all 25 Unicode whitespace characters
 * including ASCII spaces, tabs, line breaks, and Unicode spaces like non-breaking space,
 * em space, en space, etc.
 *
 * Note: Zero-width space (\u200B) is NOT part of the Unicode White_Space property
 * (it's in the "Format" category), but is explicitly included here because it behaves
 * like whitespace in text processing contexts.
 *
 * @param char - The character to check
 * @returns True if the character is a Unicode whitespace character, false otherwise
 * @example
 * isWhitespace(' ') // true
 * isWhitespace('\u00A0') // true (non-breaking space)
 * isWhitespace('\u2003') // true (em space)
 * isWhitespace('\u200B') // true (zero-width space - explicitly included)
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape MDN - Unicode character class escapes}
 * @see {@link https://unicode.org/reports/tr44/#White_Space Unicode Standard - White_Space property}
 */
export const isWhitespaceCharacter = (char: string) =>
  /\p{White_Space}|\u200B/u.test(char)

/**
 * Normalizes all Unicode whitespace characters to regular spaces.
 * This is useful for cleaning up text that may contain various Unicode whitespace characters
 * from copy-paste operations, rich text editors, or mobile input.
 *
 * Note: Zero-width space (\u200B) is NOT part of the Unicode White_Space property
 * (it's in the "Format" category), but is explicitly included here because it behaves
 * like whitespace in text processing contexts.
 *
 * @param str - The string to clean
 * @returns A new string with all Unicode whitespace normalized to regular spaces
 * @example
 * cleanSpaceCharacters('Hello\u00A0World\u2009Test') // 'Hello World Test'
 * cleanSpaceCharacters('Text\u200BWith\u2003Spaces') // 'Text With Spaces'
 */
export const cleanSpaceCharacters = (str: string) => {
  return str.replace(/[\p{White_Space}\u200B]+/gu, ' ')
}

/**
 * Removes all Unicode whitespace characters (including zero-width spaces) from a string.
 *
 * Note: Zero-width space (\u200B) is NOT part of the Unicode White_Space property
 * (it's in the "Format" category), but is explicitly included here because it behaves
 * like whitespace in text processing contexts.
 *
 * @param str - The string to remove whitespace from
 * @returns A new string with all Unicode whitespace removed
 * @example
 * removeSpaceCharacters('Hello\u00A0World\u2009Test This') // 'HelloWorldTestThis'
 */
export const removeSpaceCharacters = (str: string) =>
  str.replace(/\p{White_Space}|\u200B/gu, '')

/**
 * Normalizes text for accent-insensitive searching by removing diacritical marks.
 *
 * Uses Unicode normalization (NFD) to decompose accented characters into base characters
 * plus combining diacritical marks, then removes the diacritical marks.
 *
 * Note: This works for most accented characters that can be decomposed (like á, é, í, ó, ú, ç, ñ, ä, ë, ô)
 * but may not handle all special characters like German ß or some Nordic characters.
 *
 * @param str - The text to normalize
 * @returns Text with accents removed (e.g., "café" becomes "cafe", "Montréal" becomes "Montreal")
 */
export const normalizeTextForSearch = (str: string) =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

/**
 * Converts a camelCase or snake_case string to a human-readable label.
 *
 * @param text - The string to convert
 * @param isTitleCase - If true, capitalizes each word; if false, only capitalizes the first character
 * @returns The formatted readable label
 * @example
 * toReadableLabel('paymentMethod') // 'Payment method'
 * toReadableLabel('payment_method') // 'Payment method'
 * toReadableLabel('ChargebackReversed') // 'Chargeback reversed'
 * toReadableLabel('main_account', true) // 'Main Account'
 */
export const toReadableLabel = (text: string, isTitleCase = false) => {
  const normalized = text
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

  if (isTitleCase) {
    return normalized
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1).toLowerCase()}`
}
